<script setup lang="ts">
/**
 * Badge — branded pill/tag for property-card moments (RENTED, Premium, "Launches in 5 days", …).
 *
 * Differs from `Chip` in that its color values are a **curated decorative palette**, not the
 * kit's semantic vocabulary. Use `Chip` for status-feedback shapes (primary/success/warning/danger).
 *
 * Sizes:
 *   sm  → 23px height — card-image overlays
 *   md  → 26px height — detail gallery
 *   lg  → 28px height — premium badge
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

export type BadgeColor = 'blue' | 'navy' | 'cobalt' | 'green' | 'teal' | 'amber' | 'warning'
export type BadgeSize = 'sm' | 'md' | 'lg'

interface Props {
  label: string
  /** PrimeIcons name without the `pi-` prefix (e.g. `"calendar"`). */
  icon?: PiIconName
  color?: BadgeColor
  size?: BadgeSize
  /** Apply a backdrop blur — useful when overlaid on a property image. */
  blur?: boolean
}

withDefaults(defineProps<Props>(), {
  color: 'blue',
  size: 'md',
  blur: false,
})
</script>

<template>
  <Box
    flex
    align="center"
    :class="['badge', `badge--color-${color}`, `badge--size-${size}`, { 'badge--blur': blur }]"
  >
    <PiIcon v-if="icon" :name="icon" class="badge__icon" />
    <Text is="span" class="badge__label">{{ label }}</Text>
  </Box>
</template>

<style scoped>
.badge {
  gap: 4px;
  border-radius: 26px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Sizes ── */
.badge--size-sm {
  height: 23px;
  padding: 0 8px;
}

.badge--size-md {
  height: 26px;
  padding: 0 10px;
}

.badge--size-lg {
  height: 28px;
  padding: 0 10px;
  border-radius: 100px;
}

/* ── Blur overlay (for badges over images) ── */
.badge--blur {
  backdrop-filter: blur(2px);
}

/* ── Color variants ──
   Badge is documented (shared-infrastructure.md) as carrying a DECORATIVE
   palette — these brand-specific bgs (deeper navy / cobalt / teal than the
   semantic info / success ramps) exist precisely to mark product moments
   ("Premium", "Live", "Funded"). Promote to a `--p-badge-*` namespace in
   slicealPreset.ts if a third surface needs the same shades; until then,
   keep the bespoke values here as the single source of truth.
*/
.badge--color-blue {
  background: #1570ef;
}
.badge--color-navy {
  background: #1849a9;
}
.badge--color-cobalt {
  background: #1754cf;
}
.badge--color-green {
  background: #017d28;
}
.badge--color-teal {
  background: #00765b;
}
.badge--color-amber {
  background: #fdb022;
}
.badge--color-warning {
  background: var(--p-warning-50);
  border-radius: 8px;
}

/* ── Icon ── */
.badge__icon {
  font-size: 10px;
  flex-shrink: 0;
}

/* Default: white icon */
.badge--color-blue .badge__icon,
.badge--color-navy .badge__icon,
.badge--color-cobalt .badge__icon,
.badge--color-green .badge__icon,
.badge--color-teal .badge__icon,
.badge--color-amber .badge__icon {
  color: var(--p-surface-0);
}

.badge--color-warning .badge__icon {
  color: var(--p-warning-700);
}

.badge--size-sm .badge__icon {
  font-size: 13px;
}

.badge--size-md .badge__icon {
  font-size: 12px;
}

.badge--size-lg .badge__icon {
  font-size: 14px;
}

/* ── Label ── */
.badge__label {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
}

/* Default: white label */
.badge--color-blue .badge__label,
.badge--color-navy .badge__label,
.badge--color-cobalt .badge__label,
.badge--color-green .badge__label,
.badge--color-teal .badge__label,
.badge--color-amber .badge__label {
  color: var(--p-surface-0);
}

.badge--color-warning .badge__label {
  color: var(--p-warning-700);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
}
</style>
